import React, { useState, useEffect } from 'react';
import { X, Check, Crown, CreditCard, Loader2, Sparkles, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthorCheckoutModal = ({ isOpen, onClose, initialMembership = true, initialSubscription = true, onSuccess }) => {
  const [options, setOptions] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [includeMembership, setIncludeMembership] = useState(false);
  const [includeSubscription, setIncludeSubscription] = useState(false);
  const [subPlan, setSubPlan] = useState('monthly');

  useEffect(() => {
    if (isOpen) fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchOptions = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/author/checkout-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOptions(res.data);
      // Preseleccionar según props, pero solo lo que aún no está activo
      setIncludeMembership(initialMembership && !res.data.membership.active);
      setIncludeSubscription(initialSubscription && !res.data.subscription.active);
    } catch (e) {
      toast.error('Error al cargar las opciones de pago');
    } finally {
      setFetching(false);
    }
  };

  const sym = options?.currency_symbol || '$';
  const cur = options?.currency || 'MXN';
  const membershipPrice = options?.membership?.price || 0;
  const subMonthly = options?.subscription?.monthly;
  const subAnnual = options?.subscription?.annual;
  const selectedSub = subPlan === 'annual' ? subAnnual : subMonthly;

  const todayTotal = includeMembership ? membershipPrice : 0;

  const handleCheckout = async () => {
    if (!includeMembership && !includeSubscription) {
      toast.error('Selecciona al menos una opción para continuar');
      return;
    }
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      let res;
      if (includeMembership) {
        res = await axios.post(
          `${API}/api/payments/checkout`,
          {
            package_type: 'author_membership',
            auto_subscription_plan: includeSubscription ? subPlan : 'none',
            origin_url: window.location.origin,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        res = await axios.post(
          `${API}/api/subscriptions/create-checkout`,
          { plan_type: subPlan, origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      if (res.data.url) {
        toast.success('Redirigiendo a Stripe para completar tu pago...');
        window.location.href = res.data.url;
      } else {
        toast.error('No se pudo iniciar el pago');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="author-checkout-modal">
      <div className="bg-[#14101f] rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-amber-500/30">
        {/* Header */}
        <div className="sticky top-0 bg-[#14101f] border-b border-amber-500/20 p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Activa tu Autor ViubuxConnect</h2>
              <p className="text-white/50 text-sm">Agrega membresía y/o suscripción en una sola compra</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="author-checkout-close">
            <X className="w-6 h-6 text-white/70" />
          </button>
        </div>

        {fetching ? (
          <div className="p-16 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* MEMBRESÍA - ARRIBA (lo más importante) */}
            <div
              onClick={() => !options.membership.active && setIncludeMembership((v) => !v)}
              className={`rounded-xl p-5 border-2 transition-all ${
                options.membership.active
                  ? 'border-green-500/40 bg-green-500/5 cursor-default'
                  : includeMembership
                    ? 'border-amber-500 bg-amber-500/10 cursor-pointer'
                    : 'border-white/10 hover:border-amber-500/50 bg-black/20 cursor-pointer'
              }`}
              data-testid="cart-membership"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Crown className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      Membresía de Autor
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] font-bold rounded-full uppercase">Principal</span>
                    </h3>
                    <p className="text-white/50 text-sm">Acceso anual al Pool de Autores (42%)</p>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-white">{sym}{membershipPrice.toLocaleString()}</span>
                      <span className="text-white/50 text-sm ml-2">{cur}/año · cobro inmediato</span>
                    </div>
                  </div>
                </div>
                {options.membership.active ? (
                  <span className="text-green-400 text-xs font-bold flex items-center gap-1"><Check className="w-4 h-4" /> ACTIVA</span>
                ) : (
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${includeMembership ? 'border-amber-500 bg-amber-500' : 'border-white/30'}`}>
                    {includeMembership && <Check className="w-4 h-4 text-black" />}
                  </div>
                )}
              </div>
              {!options.membership.active && (
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                  {options.membership.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/60 text-xs">
                      <Check className="w-3 h-3 text-amber-400 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* SUSCRIPCIÓN */}
            <div
              className={`rounded-xl p-5 border-2 transition-all ${
                options.subscription.active
                  ? 'border-green-500/40 bg-green-500/5'
                  : includeSubscription
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-white/10 bg-black/20'
              }`}
              data-testid="cart-subscription"
            >
              <div
                className="flex items-start justify-between gap-4 cursor-pointer"
                onClick={() => !options.subscription.active && setIncludeSubscription((v) => !v)}
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white">Suscripción ViubuxConnect</h3>
                    <p className="text-white/50 text-sm">Monetiza el 100% del Pool · incluye ViubuxPlay</p>
                  </div>
                </div>
                {options.subscription.active ? (
                  <span className="text-green-400 text-xs font-bold flex items-center gap-1"><Check className="w-4 h-4" /> ACTIVA</span>
                ) : (
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${includeSubscription ? 'border-purple-500 bg-purple-500' : 'border-white/30'}`}>
                    {includeSubscription && <Check className="w-4 h-4 text-white" />}
                  </div>
                )}
              </div>

              {!options.subscription.active && includeSubscription && (
                <div className="mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSubPlan('monthly')}
                      data-testid="sub-plan-monthly"
                      className={`rounded-lg border px-3 py-3 text-left transition-all ${subPlan === 'monthly' ? 'border-purple-500 bg-purple-500/15' : 'border-white/15 bg-black/20 hover:border-purple-500/50'}`}
                    >
                      <p className="text-white font-semibold text-sm">Mensual</p>
                      <p className="text-white/80 text-lg font-bold">{sym}{subMonthly.price.toLocaleString()}<span className="text-white/40 text-xs font-normal"> {cur}/mes</span></p>
                      {subMonthly.discount_percent > 0 && <p className="text-green-400 text-[11px]">-{subMonthly.discount_percent}% referido</p>}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubPlan('annual')}
                      data-testid="sub-plan-annual"
                      className={`rounded-lg border px-3 py-3 text-left transition-all relative ${subPlan === 'annual' ? 'border-purple-500 bg-purple-500/15' : 'border-white/15 bg-black/20 hover:border-purple-500/50'}`}
                    >
                      <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-green-500/20 text-green-300 text-[9px] font-bold rounded">-20%</span>
                      <p className="text-white font-semibold text-sm">Anual</p>
                      <p className="text-white/80 text-lg font-bold">{sym}{subAnnual.price.toLocaleString()}<span className="text-white/40 text-xs font-normal"> {cur}/año</span></p>
                      <p className="text-green-400 text-[11px]">Mejor valor</p>
                    </button>
                  </div>
                  <div className="mt-3 bg-purple-500/10 border border-purple-500/30 rounded-lg p-2.5 text-xs text-purple-300">
                    📅 {includeMembership
                      ? 'Prueba sin cargo hoy. Tu suscripción se activa con periodo de prueba y se renueva automáticamente el día 1.'
                      : 'Se prorratea al día 1 del mes. Renovación automática.'}
                  </div>
                </div>
              )}
            </div>

            {/* RESUMEN */}
            <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/70 text-sm">A pagar hoy</span>
                <span className="text-2xl font-bold text-white" data-testid="cart-total-today">{sym}{todayTotal.toLocaleString()} {cur}</span>
              </div>
              {includeSubscription && includeMembership && (
                <p className="text-white/40 text-xs">
                  + Suscripción {subPlan === 'annual' ? 'anual' : 'mensual'} ({sym}{selectedSub?.price?.toLocaleString()} {cur}) con periodo de prueba — se cobra automáticamente después.
                </p>
              )}
              {includeSubscription && !includeMembership && (
                <p className="text-white/40 text-xs">
                  Suscripción {subPlan === 'annual' ? 'anual' : 'mensual'}: {sym}{selectedSub?.price?.toLocaleString()} {cur}. El monto de hoy puede incluir prueba/prorrateo.
                </p>
              )}

              <button
                onClick={handleCheckout}
                disabled={loading || (!includeMembership && !includeSubscription)}
                className="w-full mt-4 bg-gradient-to-r from-amber-500 to-pink-600 hover:from-amber-600 hover:to-pink-700 text-white font-bold rounded-xl py-3.5 flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                data-testid="author-checkout-pay-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                {loading ? 'Procesando...' : 'Pagar con Stripe'}
              </button>
              <p className="text-center text-white/30 text-[11px] mt-2">Pago seguro procesado por Stripe. Puedes cancelar la suscripción cuando quieras.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorCheckoutModal;
